import type { ComputedRef } from 'vue';

import {
    type BackendErrorEntity,
    type ErrorType,
    getApiAuctionsListingQueryKey,
    getApiDirectSalesListingQueryKey,
    SellingMethod,
    useApiAuctionsListing,
    useApiDirectSalesListing,
} from '~/apiClient';
import { AUCTION_LISTING_QUERY_KEY_PREFIX, DIRECT_SALE_LISTING_QUERY_KEY_PREFIX } from '~/constants/queryKeyPrefix';
import { type SaleListingItemEntity } from '~/types/sale.type';

interface TotalItems {
    [SellingMethod.Auction]: number;
    [SellingMethod.DirectSale]: number;
    current: number;
}

export interface UseSlpDataReturn {
    suspense: () => Promise<unknown>;
    sales: Ref<SaleListingItemEntity[]>;
    totalItems: Ref<TotalItems>;
    isPending: Ref<boolean>;
    error: Ref<ErrorType<BackendErrorEntity> | null>;
    refetch: () => Promise<any>;
    appliedFiltersCounter: ComputedRef<number>;
    appliedFilters: ComputedRef<AppliedFilter[]>;
    hasAppliedFilters: ComputedRef<boolean>;
}

export const useSlpData = (): UseSlpDataReturn => {
    // COMPOSABLES
    const sellingMethod = useSlpSellingMethod();

    const {
        appliedFilters: alpAppliedFilters,
        appliedFiltersCounter: alpAppliedFiltersCounter,
        hasAppliedFilters: alpHasAppliedFilters,
        apiParams: alpApiParams,
    } = useSlpParams(SellingMethod.Auction);
    const {
        appliedFilters: dslpAppliedFilters,
        appliedFiltersCounter: dslpAppliedFiltersCounter,
        hasAppliedFilters: dslpHasAppliedFilters,
        apiParams: dslpApiParams,
    } = useSlpParams(SellingMethod.DirectSale);

    // DATA
    const appliedFilters = computed(() => (sellingMethod.value === SellingMethod.Auction ? alpAppliedFilters.value : dslpAppliedFilters.value));
    const appliedFiltersCounter = computed(() => {
        switch (sellingMethod.value) {
            case SellingMethod.Auction:
                return alpAppliedFiltersCounter.value;
            case SellingMethod.DirectSale:
                return dslpAppliedFiltersCounter.value;
        }
    });
    const hasAppliedFilters = computed(() => {
        switch (sellingMethod.value) {
            case SellingMethod.Auction:
                return alpHasAppliedFilters.value;
            case SellingMethod.DirectSale:
                return dslpHasAppliedFilters.value;
        }
    });

    // DATA FETCHING
    const auctionListingApiParams = computed(() => {
        if (sellingMethod.value !== SellingMethod.Auction) {
            return {
                ...mapDslpToAlpFiltersApiParams(dslpApiParams.value),
                itemsPerPage: 0,
            };
        }

        return alpApiParams.value;
    });
    const alpKeyPrefix = computed(() => (sellingMethod.value === SellingMethod.Auction ? AUCTION_LISTING_QUERY_KEY_PREFIX : undefined));
    const {
        data: alpData,
        suspense: alpSuspense,
        isPending: isAlpPending,
        error: alpError,
        refetch: alpRefetch,
    } = useApiAuctionsListing(auctionListingApiParams, {
        query: {
            queryKey: [alpKeyPrefix, ...getApiAuctionsListingQueryKey(alpApiParams)],
        },
    });

    const directSaleListingApiParams = computed(() => {
        if (sellingMethod.value !== SellingMethod.DirectSale) {
            return {
                ...mapAlpToDslpFiltersApiParams(alpApiParams.value),
                itemsPerPage: 0,
            };
        }

        return dslpApiParams.value;
    });
    const dslpKeyPrefix = computed(() => (sellingMethod.value === SellingMethod.DirectSale ? DIRECT_SALE_LISTING_QUERY_KEY_PREFIX : undefined));
    const {
        data: dslpData,
        suspense: dslpSuspense,
        isPending: isDslpPending,
        error: dslpError,
        refetch: dslpRefetch,
    } = useApiDirectSalesListing(directSaleListingApiParams, {
        query: {
            queryKey: [dslpKeyPrefix, ...getApiDirectSalesListingQueryKey(dslpApiParams)],
        },
    });

    const totalItems = computed<TotalItems>(() => {
        const dslpTotalItems = isDslpPending.value || !dslpData.value ? 0 : dslpData.value.totalItems;
        const alpTotalItems = isAlpPending.value || !alpData.value ? 0 : alpData.value.totalItems;

        return {
            [SellingMethod.Auction]: alpTotalItems,
            [SellingMethod.DirectSale]: dslpTotalItems,
            current: sellingMethod.value === SellingMethod.Auction ? alpTotalItems : dslpTotalItems,
        };
    });

    const { sales } = useSaleListing({
        sales: toRef(() => {
            switch (sellingMethod.value) {
                case SellingMethod.Auction:
                    return alpData.value?.auctions ?? [];
                case SellingMethod.DirectSale:
                    return dslpData.value?.directSales ?? [];
            }
        }),
        sellingMethod,
    });

    const isPending = computed(() => isAlpPending.value || isDslpPending.value);
    const error = computed(() => {
        switch (sellingMethod.value) {
            case SellingMethod.Auction:
                return alpError.value;
            case SellingMethod.DirectSale:
                return dslpError.value;
        }
    });

    return {
        appliedFilters,
        appliedFiltersCounter,
        hasAppliedFilters,
        sales,
        totalItems,
        isPending,
        error,
        refetch: async () => (sellingMethod.value === SellingMethod.Auction ? alpRefetch() : dslpRefetch()),
        suspense: async () => await Promise.all([alpSuspense(), dslpSuspense()]),
    };
};
